import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BreadcrumbsBanner } from '@components/molecules/BreadcrumbsBanner';
import { PromoBasketCardProps } from '@components/molecules/PromoBasketCard';
import { PromoBasketsList } from '@components/molecules/PromoBasketsList';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Space } from '@components/atoms/Space';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { PopOver } from '@components/atoms/PopOver';
import { ProductsInBasket } from './ProductsInBasket';
import { formatPrice } from '@utils/format';
import { MenuPages } from '@types';
import styles from './BasketDetail.module.css';
import { MarketplaceAdvertising } from '@components/molecules/MarketplaceAdvertising';
import { Footer } from '@components/molecules/Footer';
import { useThemes } from '@hooks/useThemes';
import { Modules, useModule } from '@hooks/useModule';
import { useData } from '@hooks/useData';
import { ProductCardProps } from '@components/molecules/ProductCard';
import { Rating } from '@components/molecules/Rating';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useAddProductToCart } from '@services/useAddProductToCart';

export interface BasketDetailProps {
    name: string;
    merchants: string[];
    rating?: number;
    reviews?: number;
    oldPrice: number | null;
    price: number;
    points: number;
    image: {
        src: string;
    };
    description: string;
    products: ProductBundleProps[];
    relatedPromoBaskets: PromoBasketCardProps[];
}

interface ProductBundleProps extends ProductCardProps {
    quantity?: number;
    attributes?: ProductAttribute[];
}

interface ProductAttribute {
    uuid: string;
    label: string;
    values: AttributeValue[];
}

interface AttributeValue {
    uuid: string;
    value: string;
}

export const BasketDetail: FC<BasketDetailProps> = ({
    name,
    merchants,
    rating,
    reviews,
    oldPrice,
    price,
    points,
    image,
    description,
    products,
    relatedPromoBaskets,
}) => {
    const { isModuleActive } = useModule();
    const { useGrayscaleTheme } = useThemes();
    const { allSettings } = useData();
    const { t } = useTranslation();

    const translations = {
        breadcrumbsTitle: t('promo-basket-detail-component.breadcrumbs.title'),
        breadcrumbsPreviousPage: t('promo-basket-detail-component.breadcrumbs.previous-page'),
        merchantsLabel: t('promo-basket-detail-component.merchants.label'),
        pointsAmount: t('promo-basket-detail-component.order-points.amount', { count: points }),
        popoverTitle: t('promo-basket-detail-component.order-points.popover.title'),
        addToBasket: t('promo-basket-detail-component.add-to-basket'),
        relatedPromoBasketsTitle: t('promo-basket-detail-component.related-promo-baskets.title'),
    };

    const inCatalogMode = isModuleActive(Modules.catalogMode);

    //here we define the image as the bundle image
    const validProducts = products
        .filter((product) => product.uuid)
        .map((product) => ({
            uuid: product.uuid!,
            attributesValuesUuids: product.attributes
                ? product.attributes.flatMap((attr) => attr.values.map((value) => value.uuid))
                : [],
            name: product.name || '',
            image: image.src || null,
            quantity: product.quantity || 0,
        }));

    const popoverContent = allSettings?.data?.module_points_gamification?.description;

    return (
        <div className={`${useGrayscaleTheme ? styles['theme-grayscale'] : ''}`} data-testid="promo-basket-detail">
            <StickyHeader menuActivePage={MenuPages.PromoBaskets} />

            <BreadcrumbsBanner
                title={translations.breadcrumbsTitle}
                links={[{ name: translations.breadcrumbsPreviousPage, url: 'baskets' }]}
                currentPage={name}
                image={allSettings?.data?.content_headers_products_bundles_image}
            />

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <Container classes="px-3">
                <div className="row">
                    <div className="col-12 col-lg-6">
                        <img src={image.src} className="w-100" alt={name} />
                    </div>
                    <div className="col-12 col-lg-6 align-content-center">
                        <Typography
                            classes="mb-1"
                            tagVariant={TypographyTagVariants.h2}
                            appearanceLevel={TypographyAppearanceVariants.level3}>
                            {name}
                        </Typography>
                        {/* merchant */}
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-4 pb-2">
                            <div>
                                <Typography tagVariant={TypographyTagVariants.span} color="muted">
                                    {`${translations.merchantsLabel}: `}
                                </Typography>

                                {merchants.map((merchant, index) => (
                                    <Fragment key={`merchant-${index}`}>
                                        {index !== 0 && (
                                            <Typography color="muted" fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                {' ∙ '}
                                            </Typography>
                                        )}
                                        <Anchor href="merchant/x" mode={AnchorMode.none} classes={styles.link}>
                                            {merchant}
                                        </Anchor>
                                    </Fragment>
                                ))}
                            </div>
                        </div>
                        <div className="mb-4">
                            <Typography color="muted" fontSize="16">
                                {description}
                            </Typography>
                        </div>
                        <div className="mb-2">
                            <Rating rating={rating} reviews={reviews} />
                        </div>
                        {/* price */}
                        <div className="mb-2">
                            {oldPrice !== null && oldPrice > price && (
                                <div className="d-inline-block" data-testid="old-price">
                                    <Typography
                                        classes={`${styles.price} mb-0 me-4`}
                                        fontSize="36"
                                        styles={{ lineThrough: true }}>
                                        {formatPrice(oldPrice)}
                                    </Typography>
                                </div>
                            )}
                            <div className="d-inline-block" data-testid="price">
                                <Typography classes={`${styles.price} mb-0`} fontWeight="800" fontSize="36">
                                    {formatPrice(price)}
                                </Typography>
                            </div>
                        </div>
                        {/* points */}
                        {!inCatalogMode && points !== 0 && (
                            <div className={`${styles.points} d-flex align-items-center text-muted gap-2 mb-3 mb-md-4`}>
                                <Typography tagVariant={TypographyTagVariants.span} fontSize="16">
                                    <i className="fa-light fa-trophy fa-xl" />
                                </Typography>
                                <Typography classes="mb-0" fontSize="16">
                                    {translations.pointsAmount}
                                </Typography>
                                {popoverContent && (
                                    <PopOver title={translations.popoverTitle} text={popoverContent} placement="bottom">
                                        <i className="fa-light fa-circle-info fa-xl"></i>
                                    </PopOver>
                                )}
                            </div>
                        )}
                        {/* add to shopping basket */}
                        {!inCatalogMode && (
                            <div className="mb-4">
                                <AddToCart products={validProducts} disabled={false} />
                            </div>
                        )}
                    </div>
                </div>
            </Container>

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <ProductsInBasket products={products} />

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            {relatedPromoBaskets.length > 0 && (
                <PromoBasketsList title={translations.relatedPromoBasketsTitle} promoBaskets={relatedPromoBaskets} />
            )}

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <MarketplaceAdvertising />

            <Footer mode="normal" />
        </div>
    );
};

interface Product {
    uuid: string;
    attributesValuesUuids: string[];
    name: string;
    image: string | null;
    quantity?: number;
}

interface AddToCartProps {
    disabled: boolean;
    products: Product[];
}

export const AddToCart: FC<AddToCartProps> = ({ products, disabled }) => {
    const {
        request: requestAddProductToCart,
        loading,
        lastSuccessfulResponseTime,
        errorMessage,
    } = useAddProductToCart();
    const { t } = useTranslation();
    const { showProductAddedToCartNotification, showDefaultNotification } = useNotifications();

    const translations = {
        addToCart: t('promo-basket-detail-component.add-to-cart.add-to-cart'),
        feedbackAddedToCart: t('promo-basket-detail-component.add-to-cart.feedback.added-to-cart'),
    };

    const [quantities, setQuantities] = useState<Record<string, number>>({});

    useEffect(() => {
        setQuantities(products.reduce((acc, product) => ({ ...acc, [product.uuid]: product.quantity || 1 }), {}));
    }, [products]);

    const [notificationShown, setNotificationShown] = useState(false);

    useEffect(() => {
        if (errorMessage) {
            showDefaultNotification({ type: 'error', message: errorMessage });
        } else if (lastSuccessfulResponseTime !== null && !notificationShown) {
            const productList = products.map((product) => ({
                name: product.name,
                image: product.image || '',
                quantity: product.quantity,
            }));

            showProductAddedToCartNotification({
                message: translations.feedbackAddedToCart,
                product: productList,
            });
            setNotificationShown(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastSuccessfulResponseTime, notificationShown, errorMessage]);

    const handleAddToCart = () => {
        setNotificationShown(false);
        products.forEach((product) => {
            requestAddProductToCart({
                product_uuid: product.uuid,
                quantity: quantities[product.uuid],
                attributes_values_uuids: product.attributesValuesUuids,
            });
        });
    };

    return (
        <div className="d-flex flex-column gap-3" data-testid="add-to-cart">
            <div className="d-flex align-items-center gap-2">
                <Button
                    mode={ButtonMode.primary}
                    icon="fa-regular fa-basket-shopping"
                    label={translations.addToCart}
                    disabled={disabled || loading}
                    onClick={handleAddToCart}
                />
            </div>
        </div>
    );
};
